/* ==================================================
  c_store-inventory-settings.js — 店舗在庫確認モーダルの店舗設定（store_setting）取得
  - snippets/c_sq-store-inventory.liquid（商品詳細モーダル / c_shop-stock のページ埋め込み）で使う。
  - 店舗設定は App Proxy /apps/store/store-settings だけから取る（backend が全ページを辿るので
    251 件以上でも欠けない）。Liquid 注入の fallback は持たない。
  - 取得できない（HTTP エラー・タイムアウト・一覧が空）時は reject し、モーダル側は既存の
    エラー表示に倒す。非表示にすべき店舗を一瞬でも出さないため。
  - ビルド工程なしの素の IIFE。window.CStoreInventorySettings にぶら下げる（import/export しない）。
===================================================*/
(function () {
  'use strict';

  /* 応答 → Map（sq_location_id → { reserveEligible, tempClosed, hidden }）。
     - frontstore の ResponseTransformInterceptor で { success, data: { storeSettings } } に包まれる。
       非包装形（{ storeSettings }）にもフォールバックする。
     - sq_location_id 未設定のエントリは backend が空文字で返す。SQ の location.id と一致しない
       ので捨てる。
     - reserveEligible / tempClosed は同じ id なら後勝ち（従来どおり）。
     - hidden（stockCheckHidden）は同じ id のどれか 1 件でも true なら隠す。管理画面で重複
       エントリを作られても、並び順で表示/非表示が揺れないよう隠す側に倒す。
     - 未設定（backend 未対応を含む）は表示。
     一覧が無い・有効なエントリが 0 件は throw（backend は空の一覧をキャッシュしないので、
     空は Shopify 側の異常とみなす）。 */
  function parseStoreSettingsResponse(json) {
    const payload = (json && json.data) ? json.data : json;
    const list = payload && Array.isArray(payload.storeSettings) ? payload.storeSettings : null;
    if (!list) throw new Error('store-settings: storeSettings list missing');

    const map = new Map();
    list.forEach(function (s) {
      if (!s || !s.sqLocationId) return;
      const id = String(s.sqLocationId);
      const prev = map.get(id);
      map.set(id, {
        reserveEligible: s.reserveEligible === true,
        tempClosed: s.isTemporarilyClosed === true,
        hidden: s.stockCheckHidden === true || !!(prev && prev.hidden),
      });
    });
    if (map.size === 0) throw new Error('store-settings: no usable entries');
    return map;
  }

  /* 店舗在庫確認の一覧・お気に入りから外す店舗か。
     SQ が location.id を返さない店舗（合成キー）や設定の無い店舗は表示する。 */
  function isStockCheckHidden(settingsMap, locationId) {
    if (!locationId) return false;
    const s = settingsMap.get(String(locationId));
    return !!(s && s.hidden);
  }

  /* SQ getLocationAvailabilities の nodes から、店舗在庫確認に出す店舗だけを残す。
     location の無い node（不正応答）と非表示フラグの店舗を外す。順序は保つ。 */
  function filterVisibleNodes(nodes, settingsMap) {
    return nodes.filter(function (loca) {
      return !!(loca && loca.location) && !isStockCheckHidden(settingsMap, loca.location.id);
    });
  }

  /* SQ getLocationAvailabilities の nodes から、RETAIL（店舗）のロケーションだけを残す（HDN1794-1938）。
     getLocationAvailabilities は種別で絞れず、location に種別も返さないため、倉庫（WAREHOUSE）が混ざる。
     retailIds は c_sq-location-display-name.js の loadLocations() が集めた RETAIL の location.id。
     location.id の無い node（合成キー）は RETAIL か確かめられないので外す。順序は保つ。 */
  function filterRetailNodes(nodes, retailIds) {
    return nodes.filter(function (loca) {
      return !!(loca && loca.location && loca.location.id) && retailIds.has(String(loca.location.id));
    });
  }

  /* 店舗設定をページで 1 回だけ取得する loader。
     - load() は共有 promise を返す（バリアント切替のたびに取り直さない。backend 側も 30 秒キャッシュ）。
     - reject したら共有 promise を破棄し、次の load()（モーダル再オープン・バリアント切替）で取り直す。
     - タイムアウトは res.json() まで含めて timeoutMs で打ち切り、通信も abort する。
     - timeoutRetries 回まで、タイムアウトの時だけ自動で取り直す。再オープン導線の無い
       ページ埋め込みモードで、backend のキャッシュが冷えていた 1 回目に詰まらないようにするため。
     - 共有 promise には常に catch を付けておく。呼び出し側が早期 return して await しなくても
       unhandledRejection にならない。
     fetchImpl / setTimeoutImpl / clearTimeoutImpl はテスト用の差し替え口（省略時はブラウザ標準）。 */
  function createStoreSettingsLoader(options) {
    const url = options.url;
    const timeoutMs = options.timeoutMs;
    const timeoutRetries = options.timeoutRetries || 0;
    const fetchImpl = options.fetchImpl || function (u, o) { return window.fetch(u, o); };
    const setTimeoutImpl = options.setTimeoutImpl || function (fn, ms) { return setTimeout(fn, ms); };
    const clearTimeoutImpl = options.clearTimeoutImpl || function (id) { clearTimeout(id); };

    let shared = null;

    function fetchOnce() {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      let timer = null;
      const timeout = new Promise(function (_, reject) {
        timer = setTimeoutImpl(function () {
          if (controller) controller.abort();
          const err = new Error('store-settings: timeout after ' + timeoutMs + 'ms');
          err.name = 'TimeoutError';
          reject(err);
        }, timeoutMs);
      });
      const work = (async function () {
        const res = await fetchImpl(url, {
          headers: { Accept: 'application/json' },
          signal: controller ? controller.signal : undefined,
        });
        if (!res.ok) throw new Error('store-settings: HTTP ' + res.status);
        return parseStoreSettingsResponse(await res.json());
      })();
      /* タイムアウトが先に決着した後の abort による reject を握りつぶす */
      work.catch(function () {});
      return Promise.race([work, timeout]).finally(function () { clearTimeoutImpl(timer); });
    }

    async function fetchWithRetry() {
      let retries = 0;
      for (;;) {
        try {
          return await fetchOnce();
        } catch (err) {
          if (err && err.name === 'TimeoutError' && retries < timeoutRetries) {
            retries += 1;
            console.warn('[c_storeInventory] store-settings timeout — retrying');
            continue;
          }
          throw err;
        }
      }
    }

    function load() {
      if (shared) return shared;
      const p = fetchWithRetry();
      shared = p;
      p.catch(function () {
        if (shared === p) shared = null;
      });
      return p;
    }

    return { load: load };
  }

  window.CStoreInventorySettings = {
    parseStoreSettingsResponse: parseStoreSettingsResponse,
    isStockCheckHidden: isStockCheckHidden,
    filterVisibleNodes: filterVisibleNodes,
    filterRetailNodes: filterRetailNodes,
    createStoreSettingsLoader: createStoreSettingsLoader,
  };
})();
